import { Box, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LuActivity, LuCalculator, LuCircleDollarSign } from "react-icons/lu";

/**
 * PortfolioStatsCard
 * ────────────────────────────────────────────────────────────
 * Reusable vertical stack of statistical metric cards for a portfolio.
 * Displays: GWP (Mean), GWP (SD), Mean Cost.
 */

const mean = (arr) => (arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

const sd = (arr) => {
  if (arr.length < 2) return 0;
  const avg = mean(arr);
  const variance = arr.reduce((sum, v) => sum + Math.pow(v - avg, 2), 0) / (arr.length - 1);
  return Math.sqrt(variance);
};

const computeStats = (items = []) => {
  const gwpValues = items.map((i) => Number(i.gwp) || 0).filter((v) => v > 0);
  const costValues = items.map((i) => parseFloat(i.cost) || 0).filter((v) => v > 0);
  return {
    gwpMean: mean(gwpValues),
    gwpSD: sd(gwpValues),
    costMean: mean(costValues),
    count: items.length,
  };
};

const fmt = (val) =>
  val.toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 1 });

// Pure standard AgGrid text
const stdText = (isDark) => ({
  fontFamily: "Poppins, sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  color: isDark ? "#f1f5f9" : "#1e293b",
});

// ── Mini Metric Card ───────────────────────────────────────────────────────────
const MiniMetricCard = ({ label, value, unit, tooltip, icon: Icon, accentColor, bgOverlay, isDark }) => (
  <Tooltip title={tooltip || ""} arrow placement="left">
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1.5,
        borderRadius: "10px",
        border: `1px solid ${isDark ? "#3f3f46" : "#e5e7eb"}`,
        bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#ffffff",
        transition: "all 0.2s ease",
        cursor: "default",
        "&:hover": {
          bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
          transform: "translateY(-1px)",
          boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.4)" : "0 4px 12px rgba(0,0,0,0.06)",
        },
      }}
    >
      {/* Icon & Label */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            bgcolor: bgOverlay,
            color: accentColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={18} strokeWidth={2.5} />
        </Box>
        <Typography sx={stdText(isDark)}>{label}</Typography>
      </Box>

      {/* Value & Unit */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography sx={{ ...stdText(isDark), fontWeight: 600, fontSize: "16px", color: isDark ? "#ffffff" : "#0f172a" }}>
          {fmt(value)}
        </Typography>
        <Typography sx={{ ...stdText(isDark), fontSize: "13px", color: isDark ? "#94a3b8" : "#64748b" }}>
          {unit}
        </Typography>
      </Box>
    </Box>
  </Tooltip>
);

// ── Main component ────────────────────────────────────────────────────────────
const PortfolioStatsCard = ({ items = [], isDark, direction = "column", hideHeader = false }) => {
  const { t } = useTranslation();
  const { gwpMean, gwpSD, costMean, count } = computeStats(items);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Section Header */}
      {!hideHeader && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography sx={{ ...stdText(isDark), fontWeight: 500 }}>
            {t("Portfolio statistics")}
          </Typography>
          <Typography sx={{ ...stdText(isDark), fontSize: "13px", color: isDark ? "#94a3b8" : "#64748b" }}>
            n = {count}
          </Typography>
        </Box>
      )}

      {/* Stack of Cards */}
      <Box sx={{ display: "flex", flexDirection: direction, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <MiniMetricCard
            label={t("GWP (Mean)")}
            value={gwpMean}
            unit="kg CO₂e"
            tooltip={t("Average GWP across the selection")}
            icon={LuCalculator}
            accentColor="#16a34a"
            bgOverlay="rgba(22, 163, 74, 0.12)"
            isDark={isDark}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <MiniMetricCard
            label={t("GWP (SD)")}
            value={gwpSD}
            unit="kg CO₂e"
            tooltip={t("Standard deviation of GWP across the selection")}
            icon={LuActivity}
            accentColor="#3b82f6"
            bgOverlay="rgba(59, 130, 246, 0.12)"
            isDark={isDark}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <MiniMetricCard
            label={t("Mean Cost")}
            value={costMean}
            unit="$"
            tooltip={t("Average declared cost across the selection")}
            icon={LuCircleDollarSign}
            accentColor="#f59e0b"
            bgOverlay="rgba(245, 158, 11, 0.12)"
            isDark={isDark}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PortfolioStatsCard;
